import React from 'react';
import { Box, Avatar, Typography, Chip } from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import { renderMessageLine } from './messageRendering';
import { headerGradient, assistantAvatarSx } from './chatConstants';
import { AIChatFeedback } from './AIChatFeedback';

export function ChatMessageBubble({
    message,
    isTyping,
    onSuggestionClick,
    schoolClasses,
    schoolStudents,
    userId,
    profile,
}) {
    return (
        <Box
            sx={{
                display: 'flex',
                gap: 1,
                flexDirection: message.role === 'user' ? 'row-reverse' : 'row',
                animation: 'fadeIn 0.25s ease-out',
                opacity: message.dismissed ? 0.6 : 1,
                alignItems: 'flex-end',
            }}
        >
            <Avatar
                variant='circular'
                alt={profile && profile.name}
                className={
                    ['blue', 'brown', 'red'][
                        profile &&
                            profile.name &&
                            profile.name
                                .split(' ')
                                .map((substring) => substring[0])
                                .join('')[0]
                                .toUpperCase()
                                .charCodeAt(0) % 3
                    ]
                }
                sx={{
                    ...(message.role === 'assistant' ? assistantAvatarSx : {}),
                    ...(message.role !== 'assistant'
                        ? {
                              width: 32,
                              height: 32,
                              flexShrink: 0,
                              bgcolor: 'rgba(0,0,0,0.26)',
                              color: 'white',
                          }
                        : {}),
                }}
            >
                {message.role === 'assistant' ? (
                    <SmartToyIcon sx={{ fontSize: 18 }} />
                ) : (
                    profile &&
                    profile.name &&
                    profile.name
                        .split(' ')
                        .map((substring) => substring[0])
                        .join('')
                )}
            </Avatar>

            <Box
                sx={{
                    maxWidth: '85%',
                    ...(message.role === 'user'
                        ? {
                              background: headerGradient,
                              color: '#fff',
                              borderRadius: '18px 18px 4px 18px',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                          }
                        : {
                              bgcolor: '#fff',
                              color: 'text.primary',
                              border: '1px solid rgba(0,0,0,0.08)',
                              borderRadius: '4px 18px 18px 18px',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                          }),
                    p: 1.25,
                    px: 1.5,
                }}
            >
                <Box sx={{ fontSize: '0.9rem', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                    {message?.content?.split('\n').map((line, i) => (
                        <Box key={i}>{renderMessageLine(line, schoolClasses, schoolStudents, i)}</Box>
                    ))}
                </Box>
                {message.role === 'assistant' && (
                    <Typography
                        variant='caption'
                        sx={{ display: 'block', mt: 0.5, color: 'text.secondary', fontSize: '0.7rem' }}
                    >
                        Fae • Life Skills GO AI Agent
                    </Typography>
                )}
                {message.widget && message.dismissed && (
                    <Typography
                        variant='caption'
                        sx={{ display: 'block', mt: 0.5, fontStyle: 'italic', color: 'text.secondary' }}
                    >
                        Element dismissed
                    </Typography>
                )}
                {message.suggestedPrompts && message.suggestedPrompts.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.25 }}>
                        {message.suggestedPrompts.map((suggestion, i) => (
                            <Chip
                                key={i}
                                label={renderMessageLine(suggestion?.text, schoolClasses, schoolStudents, i)}
                                size='small'
                                disabled={isTyping}
                                onClick={() => !isTyping && onSuggestionClick(message.id, suggestion.prompt)}
                                sx={{
                                    borderRadius: '9999px',
                                    py: 0.75,
                                    px: 1.25,
                                    fontSize: '0.8rem',
                                    fontWeight: 500,
                                    background: headerGradient,
                                    border: 'none',
                                    color: '#fff',
                                    fontFamily: 'var(--font-body)',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.12)',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        filter: 'brightness(1.08)',
                                        boxShadow: '0 2px 8px rgba(13, 71, 161, 0.35)',
                                        transform: 'translateY(-1px)',
                                    },
                                    ...(isTyping && { opacity: 0.7, cursor: 'not-allowed' }),
                                }}
                            />
                        ))}
                    </Box>
                )}
                {message.role === 'assistant' &&
                    (message.feedbackIds?.conversationId || message.feedbackIds?.exchangeId) && (
                        <AIChatFeedback
                            conversationId={message.feedbackIds.conversationId}
                            exchangeId={message.feedbackIds.exchangeId}
                            userId={userId}
                        />
                    )}
            </Box>
        </Box>
    );
}
